import React, { useState, useEffect, useRef } from 'react';
import { Mic, MicOff, Video, VideoOff, GripHorizontal, Minimize2, Maximize2, Smartphone, Monitor } from 'lucide-react';
import { Participant } from '../types';

interface SelfVideoPopupProps {
  localParticipant: Participant;
  localStream: MediaStream | null;
  isAudioMuted: boolean;
  isVideoOff: boolean;
  onToggleAudio: () => void;
  onToggleVideo: () => void;
}

export const SelfVideoPopup: React.FC<SelfVideoPopupProps> = ({
  localParticipant,
  localStream,
  isAudioMuted,
  isVideoOff,
  onToggleAudio,
  onToggleVideo,
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const popupRef = useRef<HTMLDivElement | null>(null);

  // Default to vertical (portrait) orientation on mobile screens (< 640px)
  const [isVertical, setIsVertical] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < 640 || window.innerHeight > window.innerWidth;
  });

  const [isMinimized, setIsMinimized] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const dragOffsetRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  // Get current dimensions based on state & screen size
  const getDimensions = () => {
    const isMobile = typeof window !== 'undefined' ? window.innerWidth < 640 : false;
    if (isMinimized) {
      return { width: isMobile ? 140 : 180, height: 42 };
    }
    if (isVertical) {
      // Portrait 9:16 ratio matching phone cameras
      return {
        width: isMobile ? 112 : 144,
        height: isMobile ? 199 : 256,
      };
    }
    // Landscape 16:9 ratio for desktop
    return {
      width: isMobile ? 180 : 256,
      height: isMobile ? 102 : 144,
    };
  };

  // Position initialized safely on screen
  const [position, setPosition] = useState<{ x: number; y: number }>(() => {
    if (typeof window === 'undefined') return { x: 20, y: 80 };
    const isMobile = window.innerWidth < 640;
    const { width, height } = { width: isMobile ? 112 : 256, height: isMobile ? 199 : 144 };

    // On mobile, dock to top-right below the header to keep stage open
    if (isMobile) {
      return {
        x: Math.max(8, window.innerWidth - width - 12),
        y: 56,
      };
    }
    // On desktop, dock to bottom-right above control bar
    return {
      x: Math.max(20, window.innerWidth - width - 24),
      y: Math.max(80, window.innerHeight - height - 100),
    };
  });

  useEffect(() => {
    if (videoRef.current && localStream) {
      videoRef.current.srcObject = localStream;
    }
  }, [localStream, isVideoOff, isMinimized, isVertical]);

  // Adjust position if window is resized or orientation changes
  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth < 640;
      // Auto-switch to vertical on mobile if not manually overridden
      setIsVertical(isMobile || window.innerHeight > window.innerWidth);

      setPosition((prev) => {
        const { width, height } = getDimensions();
        const maxX = Math.max(8, window.innerWidth - width - 8);
        const maxY = Math.max(52, window.innerHeight - height - 76);
        return {
          x: Math.min(Math.max(8, prev.x), maxX),
          y: Math.min(Math.max(52, prev.y), maxY),
        };
      });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMinimized, isVertical]);

  // Handle pointer down on drag handle
  const handlePointerDown = (e: React.PointerEvent) => {
    if (e.button !== 0) return;
    setIsDragging(true);
    dragOffsetRef.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isDragging) return;
    const { width, height } = getDimensions();
    const minX = 8;
    const maxX = Math.max(minX, window.innerWidth - width - 8);
    const minY = 52; // Below top header
    const maxY = Math.max(minY, window.innerHeight - height - 76); // Above control bar

    const nextX = Math.min(Math.max(e.clientX - dragOffsetRef.current.x, minX), maxX);
    const nextY = Math.min(Math.max(e.clientY - dragOffsetRef.current.y, minY), maxY);

    setPosition({ x: nextX, y: nextY });
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    if (isDragging) {
      setIsDragging(false);
      try {
        (e.target as HTMLElement).releasePointerCapture(e.pointerId);
      } catch {}
    }
  };

  const initials = (localParticipant.name || 'You')
    .split(' ')
    .map((n) => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const isSpeaking = Boolean(localParticipant.speaking);

  return (
    <div
      ref={popupRef}
      style={{
        transform: `translate3d(${position.x}px, ${position.y}px, 0)`,
        touchAction: 'none',
      }}
      className={`fixed top-0 left-0 z-30 select-none transition-shadow ${
        isDragging ? 'cursor-grabbing shadow-2xl ring-2 ring-blue-500/50' : 'cursor-default shadow-xl'
      }`}
    >
      <div
        className={`bg-[#0d111c]/95 backdrop-blur-xl border rounded-2xl overflow-hidden transition-all duration-200 ${
          isSpeaking
            ? 'border-emerald-500/80 shadow-[0_0_20px_rgba(16,185,129,0.25)]'
            : 'border-slate-700/80'
        } ${
          isMinimized
            ? 'w-36 sm:w-44 p-2'
            : isVertical
            ? 'w-28 sm:w-36'
            : 'w-48 sm:w-64'
        }`}
      >
        {/* Drag Handle & Control Header */}
        <div
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          className="flex items-center justify-between px-2.5 py-1 bg-[#141926] cursor-grab active:cursor-grabbing border-b border-slate-800/80 text-[11px] text-slate-300"
          title="Drag to reposition self-view"
        >
          <div className="flex items-center space-x-1 font-medium truncate">
            <GripHorizontal className="w-3 h-3 text-slate-400 flex-shrink-0" />
            <span className="truncate text-[10px] sm:text-xs">You</span>
          </div>

          <div className="flex items-center space-x-1" onPointerDown={(e) => e.stopPropagation()}>
            {/* Orientation Toggle: Vertical (Portrait) vs Horizontal (Landscape) */}
            {!isMinimized && (
              <button
                type="button"
                onClick={() => setIsVertical(!isVertical)}
                title={isVertical ? 'Switch to horizontal view' : 'Switch to vertical (phone) view'}
                className="p-1 rounded-md hover:bg-slate-700/70 text-slate-400 hover:text-white transition"
              >
                {isVertical ? <Monitor className="w-3 h-3" /> : <Smartphone className="w-3 h-3" />}
              </button>
            )}

            {/* Minimize / Expand Toggle */}
            <button
              type="button"
              onClick={() => setIsMinimized(!isMinimized)}
              title={isMinimized ? 'Expand self-view' : 'Minimize self-view'}
              className="p-1 rounded-md hover:bg-slate-700/70 text-slate-400 hover:text-white transition"
            >
              {isMinimized ? <Maximize2 className="w-3 h-3" /> : <Minimize2 className="w-3 h-3" />}
            </button>
          </div>
        </div>

        {/* Video Stage / Minimized State */}
        {!isMinimized ? (
          <div
            className={`relative w-full bg-[#121622] flex items-center justify-center overflow-hidden ${
              isVertical ? 'aspect-[9/16]' : 'aspect-video'
            }`}
          >
            {!isVideoOff && localStream ? (
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover scale-x-[-1]"
              />
            ) : (
              <div className="flex flex-col items-center justify-center space-y-1 text-slate-400 p-2">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-slate-800 flex items-center justify-center text-xs font-semibold text-slate-300 border border-slate-700">
                  {initials || 'U'}
                </div>
                <span className="text-[9px] text-slate-500">Camera off</span>
              </div>
            )}

            {/* Speaking indicator halo */}
            {isSpeaking && (
              <div className="absolute inset-0 pointer-events-none border-2 border-emerald-400 animate-pulse" />
            )}

            {/* Quick mini-controls overlay */}
            <div className="absolute bottom-1.5 left-1.5 right-1.5 flex items-center justify-between">
              <span className="text-[9px] bg-black/60 backdrop-blur-md px-1.5 py-0.5 rounded text-white font-medium truncate max-w-[65px] sm:max-w-[100px]">
                {localParticipant.name || 'You'}
              </span>

              <div className="flex items-center space-x-1">
                <button
                  type="button"
                  onClick={onToggleAudio}
                  title={isAudioMuted ? 'Unmute' : 'Mute'}
                  className={`p-1 rounded text-white backdrop-blur transition ${
                    isAudioMuted ? 'bg-rose-500/80 hover:bg-rose-600' : 'bg-black/60 hover:bg-black/80'
                  }`}
                >
                  {isAudioMuted ? <MicOff className="w-2.5 h-2.5 text-white" /> : <Mic className="w-2.5 h-2.5 text-emerald-400" />}
                </button>
                <button
                  type="button"
                  onClick={onToggleVideo}
                  title={isVideoOff ? 'Turn on camera' : 'Turn off camera'}
                  className={`p-1 rounded text-white backdrop-blur transition ${
                    isVideoOff ? 'bg-rose-500/80 hover:bg-rose-600' : 'bg-black/60 hover:bg-black/80'
                  }`}
                >
                  {isVideoOff ? <VideoOff className="w-2.5 h-2.5 text-white" /> : <Video className="w-2.5 h-2.5 text-white" />}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between text-xs py-0.5">
            <span className="text-slate-300 text-[10px] font-medium truncate">{localParticipant.name}</span>
            <div className="flex items-center space-x-1">
              <span className={`p-0.5 rounded ${isAudioMuted ? 'text-rose-400' : 'text-emerald-400'}`}>
                {isAudioMuted ? <MicOff className="w-2.5 h-2.5" /> : <Mic className="w-2.5 h-2.5" />}
              </span>
              <span className={`p-0.5 rounded ${isVideoOff ? 'text-rose-400' : 'text-slate-300'}`}>
                {isVideoOff ? <VideoOff className="w-2.5 h-2.5" /> : <Video className="w-2.5 h-2.5" />}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
